/**
 * Service pour gérer les tokens push OneSignal
 * Avec retry mechanism pour attendre l'initialisation de OneSignal
 * 
 * ✅ Best Practices:
 * - Timeout sur les edge functions (10s)
 * - Retry avec backoff exponentiel
 * - Deduplication des appels via localStorage
 * - Error handling enrichi avec contexte
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import medianNativeBridge, { loginOneSignal, grantOneSignalPrivacyConsent, isOneSignalBridgeMute } from '@/utils/medianNativeBridge';

/**
 * ✅ RGPD Guard: Vérifie que l'utilisateur a un consentement data_processing actif
 * Interroge consent_logs pour s'assurer que register() ne sera jamais appelé sans consentement
 */
async function hasRgpdConsent(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('consent_logs')
      .select('consent_given, revoked_at')
      .eq('user_id', userId)
      .eq('consent_type', 'data_processing')
      .eq('consent_given', true)
      .is('revoked_at', null)
      // Un compte peut porter plusieurs consentements actifs (renouvellement) :
      // sans limite, maybeSingle() rend une erreur et l'inscription est refusée.
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return false;
    return true;
  } catch {
    return false;
  }
}

// ✅ BEST PRACTICE: Timeout et retry pour les edge functions critiques
const EDGE_FUNCTION_TIMEOUT = 10000; // 10 secondes
const MAX_EDGE_RETRIES = 3;

/**
 * Invoque une edge function avec timeout et retry automatique
 */
async function invokeWithTimeout<T>(
  functionName: string, 
  body: Record<string, unknown>,
  options: { timeout?: number; maxRetries?: number } = {}
): Promise<{ data: T | null; error: Error | null }> {
  const timeout = options.timeout ?? EDGE_FUNCTION_TIMEOUT;
  const maxRetries = options.maxRetries ?? MAX_EDGE_RETRIES;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const { data, error } = await supabase.functions.invoke<T>(functionName, { body });
      
      clearTimeout(timeoutId);
      
      if (error) {
        // Check if it's a retryable error
        const isRetryable = !error.message?.toLowerCase().includes('unauthorized') &&
                           !error.message?.toLowerCase().includes('forbidden') &&
                           !error.message?.toLowerCase().includes('not found');
        
        if (!isRetryable || attempt === maxRetries) {
          logger.warn(`[invokeWithTimeout] ${functionName} failed after ${attempt} attempt(s):`, {
            message: error.message,
            code: (error as any).code || 'unknown'
          });
          return { data: null, error: error as Error };
        }
        
        // Wait before retry with exponential backoff
        const delay = 1000 * attempt;
        logger.debug(`[invokeWithTimeout] ${functionName} retry ${attempt}/${maxRetries} in ${delay}ms`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      
      return { data, error: null };
    } catch (err) {
      if (attempt === maxRetries) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error(`[invokeWithTimeout] ${functionName} failed after ${attempt} attempts:`, error);
        return { data: null, error };
      }
      
      // Retry on network errors
      const delay = 1000 * attempt;
      logger.debug(`[invokeWithTimeout] ${functionName} error, retry ${attempt}/${maxRetries} in ${delay}ms`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  
  return { data: null, error: new Error('Max retries exceeded') };
}

// Configuration du retry - Valeurs par défaut (Android/FCM)
const DEFAULT_MAX_RETRIES = 4;
const DEFAULT_BASE_DELAY_MS = 2000; // 2s, 4s, 6s, 8s

// ✅ iOS nécessite plus de temps (APNs plus lent que FCM) - AUGMENTÉ pour v5 API
const IOS_MAX_RETRIES = 8;      // Was 6 - more retries for APNs
const IOS_BASE_DELAY_MS = 4000; // Was 3000 - longer base delay

// Type pour le résultat détaillé de l'enregistrement
export interface PushRegistrationResult {
  success: boolean;
  attempt: number;
  playerId?: string;        // onesignal_id (user ID)
  subscriptionId?: string;  // subscription_id (device ID)
  externalIdSet: boolean;
  dbUpsertSuccess: boolean;
  error?: string;
  oneSignalInfo?: {
    userId?: string;           // onesignal_id
    subscriptionId?: string;   // subscription_id (device)
    pushToken?: string;
    subscribed?: boolean;
    externalId?: string;
  };
}

export const pushTokenService = {
  /**
   * Enregistre le token push OneSignal avec retry automatique
   * OneSignal peut prendre quelques secondes à s'initialiser après register()
   */
  async registerPushToken(userId: string): Promise<boolean> {
    logger.info('[pushTokenService] 🚀 === Starting push token registration ===');
    logger.info('[pushTokenService] User ID:', userId);
    
    // Check 1: Is Median Native?
    const isNative = medianNativeBridge.isMedianNative();
    logger.info('[pushTokenService] Is Median Native:', isNative);
    
    if (!isNative) {
      logger.warn('[pushTokenService] ❌ Not in Median native environment, aborting');
      return false;
    }
    
    // Check 2: Is OneSignal API available?
    const hasOneSignalAPI = typeof window !== 'undefined' && !!window.median?.onesignal;
    logger.info('[pushTokenService] OneSignal API available:', hasOneSignalAPI);
    
    if (!hasOneSignalAPI) {
      logger.warn('[pushTokenService] ❌ OneSignal API not available');
      return false;
    }
    
    // Check 2 bis : pont iOS muet. Les builds 71 à 77 de la coque iOS maison
    // répondent {} à info() et ne donneront jamais d'identifiant. Sans cette
    // sortie, la boucle plus bas attend 144 s à chaque connexion et le bouton
    // « Activer sur ce téléphone » des réglages reste en chargement.
    if (await isOneSignalBridgeMute()) {
      logger.warn('[pushTokenService] ❌ Pont OneSignal iOS muet (coque antérieure aux notifications), abandon');
      return false;
    }

    // Check 3: RGPD consent (data_processing must be accepted and not revoked)
    const hasConsent = await hasRgpdConsent(userId);
    if (!hasConsent) {
      logger.warn('[pushTokenService] ❌ RGPD: No data_processing consent, aborting push registration');
      return false;
    }
    // ✅ Grant privacy consent to Median/OneSignal bridge (required for Manual mode)
    grantOneSignalPrivacyConsent();
    logger.info('[pushTokenService] ✅ RGPD consent confirmed, proceeding with registration');

    // Détecter la plateforme pour adapter le timing
    const platform = medianNativeBridge.getMedianPlatform() || 'unknown';
    const isIOS = platform === 'ios';
    
    // Configuration adaptée selon la plateforme
    const maxRetries = isIOS ? IOS_MAX_RETRIES : DEFAULT_MAX_RETRIES;
    const baseDelayMs = isIOS ? IOS_BASE_DELAY_MS : DEFAULT_BASE_DELAY_MS;
    
    logger.info(`[pushTokenService] Platform: ${platform} | ${isIOS ? '🍎 iOS (APNs)' : '🤖 Android (FCM)'}`);
    logger.info(`[pushTokenService] Config: ${maxRetries} retries, ${baseDelayMs}ms base delay`);
    
    // Step 1: Call register() first to request permissions
    logger.info('[pushTokenService] 📲 Calling OneSignal register()...');
    medianNativeBridge.registerPushNotifications();
    
    // Step 2: Sur Android, appeler login() maintenant (FCM est rapide)
    // Sur iOS, attendre que OneSignal soit initialisé (APNs plus lent)
    let externalIdSet = false;
    
    if (!isIOS) {
      logger.info('[pushTokenService] 🤖 Android: Calling OneSignal login() immediately...');
      loginOneSignal(userId);
      externalIdSet = true;
      logger.info('[pushTokenService] ✅ OneSignal login() called with:', userId);
    } else {
      logger.info('[pushTokenService] 🍎 iOS: Will call OneSignal login() after initialization...');
    }
    
    logger.debug('[pushTokenService] Starting retry loop for OneSignal info...');
    
    // Retry avec délai croissant pour attendre l'initialisation
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const delay = baseDelayMs * attempt;
      logger.debug(`[pushTokenService] ⏳ Waiting ${delay}ms before attempt ${attempt}/${maxRetries}...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        const oneSignalInfo = await medianNativeBridge.getOneSignalInfo();
        
        logger.info(`[pushTokenService] Attempt ${attempt}/${maxRetries}:`, {
          userId: oneSignalInfo?.userId ? '✅' : '❌',
          subscriptionId: oneSignalInfo?.subscriptionId ? '✅' : '❌',
          pushToken: oneSignalInfo?.pushToken ? '✅' : '❌',
          externalIdSet: externalIdSet ? '✅' : '⏳',
          platform
        });
        
        if (oneSignalInfo?.userId) {
          // 🍎 iOS CRITIQUE: Vérifier que subscription_id est présent avant de continuer
          if (isIOS && !oneSignalInfo.subscriptionId) {
            logger.warn(`[pushTokenService] 🍎 iOS: userId OK mais subscription_id manquant - APNs pas encore prêt, réessai...`);
            continue; // Réessayer au prochain cycle
          }
          
          // 🍎 iOS: Appeler login() maintenant que OneSignal est initialisé
          if (isIOS && !externalIdSet) {
            logger.info('[pushTokenService] 🍎 iOS: OneSignal prêt, appel login()...');
            loginOneSignal(userId);
            externalIdSet = true;
            await new Promise(resolve => setTimeout(resolve, 500)); // Court délai pour propagation
            logger.info('[pushTokenService] ✅ iOS OneSignal login() called');
          }
          
          // MULTI-ACCOUNT: Désactiver les tokens de ce device pour AUTRES utilisateurs
          // ✅ CRITIQUE: Utiliser subscription_id (Device ID stable) car onesignal_user_id
          // change avec Identity Model v5 (chaque login() génère un nouvel ID)
          logger.info('[pushTokenService] 🔄 Deactivating tokens for other users on this device...');
          
          let cleanupQuery = supabase
            .from('user_push_tokens')
            .update({ 
              is_active: false, 
              updated_at: new Date().toISOString() 
            })
            .neq('user_id', userId);
          
          // Préférer subscription_id (stable par device) si disponible
          if (oneSignalInfo.subscriptionId) {
            cleanupQuery = cleanupQuery.eq('subscription_id', oneSignalInfo.subscriptionId);
            logger.debug('[pushTokenService] 🔄 Cleanup by subscription_id (Device ID):', 
              oneSignalInfo.subscriptionId.slice(0, 8) + '...');
          } else {
            // Fallback: onesignal_user_id (cas legacy ou si subscriptionId pas encore disponible)
            cleanupQuery = cleanupQuery.eq('onesignal_user_id', oneSignalInfo.userId);
            logger.debug('[pushTokenService] 🔄 Cleanup by onesignal_user_id (Legacy):', 
              oneSignalInfo.userId.slice(0, 8) + '...');
          }
          
          const { error: deactivateError, count: deactivatedCount } = await cleanupQuery;

          if (deactivateError) {
            logger.warn('[pushTokenService] ⚠️ Warning: Could not deactivate old tokens for this device:', deactivateError);
          } else if (deactivatedCount && deactivatedCount > 0) {
            logger.info(`[pushTokenService] ✅ Deactivated ${deactivatedCount} token(s) from other users on this device`);
          }
          
          logger.info('[pushTokenService] 💾 Upserting token to database...');
          logger.debug('[pushTokenService] Upsert data:', {
            user_id: userId,
            onesignal_user_id: oneSignalInfo.userId,
            subscription_id: oneSignalInfo.subscriptionId || null,
            platform,
            is_active: true
          });
          
          // ✅ FIX MULTI-DEVICE: Utiliser subscription_id comme clé unique par device
          const tokenData = {
            user_id: userId,
            onesignal_user_id: oneSignalInfo.userId,
            subscription_id: oneSignalInfo.subscriptionId || null,
            platform,
            is_active: true,
            last_used_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          let error: any = null;

          if (oneSignalInfo.subscriptionId) {
            // Multi-device safe: chaque device (subscription_id) = sa propre ligne
            const result = await supabase
              .from('user_push_tokens')
              .upsert(tokenData, { onConflict: 'user_id,subscription_id' });
            error = result.error;

            // Retry sur unique_violation (race condition multi-compte)
            if (error?.code === '23505') {
              logger.warn('[pushTokenService] ⚠️ unique_violation on upsert, forcing cleanup & retry...');
              await supabase
                .from('user_push_tokens')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('subscription_id', oneSignalInfo.subscriptionId)
                .neq('user_id', userId);
              const retry = await supabase
                .from('user_push_tokens')
                .upsert(tokenData, { onConflict: 'user_id,subscription_id' });
              error = retry.error;
            }
          } else {
            // LEGACY FALLBACK: pas de subscription_id
            // UNIQUE (user_id, subscription_id) ne matche pas sur NULL => SELECT + INSERT/UPDATE
            logger.warn('[pushTokenService] ⚠️ No subscription_id, using legacy insert/update');
            const { data: existing } = await supabase
              .from('user_push_tokens')
              .select('id')
              .eq('user_id', userId)
              .eq('onesignal_user_id', oneSignalInfo.userId)
              .maybeSingle();

            if (existing) {
              const result = await supabase.from('user_push_tokens')
                .update(tokenData).eq('id', existing.id);
              error = result.error;
            } else {
              const result = await supabase.from('user_push_tokens')
                .insert(tokenData);
              error = result.error;
            }
          }

          if (!error) {
            logger.info('[pushTokenService] ✅ Token saved to DB, now syncing with OneSignal server...');
            
            // ✅ FIX: Sauvegarder l'ID OneSignal pour utilisation lors du logout
            try {
              localStorage.setItem('last_onesignal_user_id', oneSignalInfo.userId);
              if (oneSignalInfo.subscriptionId) {
                localStorage.setItem('last_onesignal_subscription_id', oneSignalInfo.subscriptionId);
              }
            } catch {
              // Ignore localStorage errors
            }
            
            // ✅ BEST PRACTICE: Sync with timeout and retry
            // Sync external_id with OneSignal server-side (ensures it's set even if client failed)
            try {
              const { data: syncResult, error: syncError } = await invokeWithTimeout<{
                synced?: boolean;
                tokenValid?: boolean;
                message?: string;
              }>('sync-push-token-onesignal', { 
                userId, 
                onesignalPlayerId: oneSignalInfo.userId,
                subscriptionId: oneSignalInfo.subscriptionId,
                autoSync: true
              }, { timeout: 15000, maxRetries: 3 });
              
              if (syncError) {
                logger.warn('[pushTokenService] ⚠️ Server sync warning:', {
                  message: syncError.message,
                  code: (syncError as any).code || 'unknown'
                });
              } else if (syncResult?.synced) {
                logger.info('[pushTokenService] ✅ Server sync confirmed!');
              } else if (!syncResult?.tokenValid) {
                logger.warn('[pushTokenService] ⚠️ Token marked invalid by server:', syncResult?.message);
              } else {
                logger.info('[pushTokenService] ℹ️ Server sync result:', syncResult?.message);
              }
            } catch (syncErr) {
              logger.warn('[pushTokenService] ⚠️ Server sync failed (non-blocking):', syncErr);
            }
            
            logger.info('[pushTokenService] ✅ SUCCESS! Token registered:', {
              onesignalId: oneSignalInfo.userId.substring(0, 8) + '...',
              subscriptionId: oneSignalInfo.subscriptionId ? oneSignalInfo.subscriptionId.substring(0, 8) + '...' : 'N/A',
              platform,
              attempt,
              externalIdSet: true
            });
            
            return true;
          } else {
            logger.error('[pushTokenService] ❌ Supabase upsert error:', {
              code: error.code,
              message: error.message,
              details: error.details
            });
          }
        } else {
          logger.warn(`[pushTokenService] Attempt ${attempt}: OneSignal userId not yet available`);
        }
      } catch (error) {
        logger.warn(`[pushTokenService] Attempt ${attempt} error:`, error);
      }
    }
    
    logger.error(`[pushTokenService] ❌ FAILED after ${maxRetries} retries - OneSignal info not complete`);
    logger.info(`[pushTokenService] 💡 Platform: ${platform} | External ID was ${externalIdSet ? 'set' : 'NOT set'}`);
    
    // Conseil spécifique iOS
    if (isIOS) {
      logger.info('[pushTokenService] 🍎 iOS Troubleshooting:');
      logger.info('   1. Vérifier APNs Auth Key .p8 dans OneSignal Dashboard');
      logger.info('   2. Vérifier Push Notifications capability dans Xcode');
      logger.info('   3. Vérifier que le Bundle ID correspond');
      logger.info('   4. Réinstaller l\'app si token corrompu');
    }
    
    return false;
  },

  /**
   * Version détaillée de l'enregistrement avec résultat complet pour debug
   */
  async registerPushTokenDetailed(userId: string): Promise<PushRegistrationResult> {
    const result: PushRegistrationResult = {
      success: false,
      attempt: 0,
      externalIdSet: false,
      dbUpsertSuccess: false
    };
    
    logger.info('[pushTokenService] 🔍 Detailed registration starting for:', userId);
    
    const isNative = medianNativeBridge.isMedianNative();
    if (!isNative) {
      result.error = 'Not in Median native environment';
      return result;
    }
    
    const hasOneSignalAPI = typeof window !== 'undefined' && !!window.median?.onesignal;
    if (!hasOneSignalAPI) {
      result.error = 'OneSignal API not available';
      return result;
    }

    // Même sortie que registerPushToken : coque iOS dont le pont répond {}.
    if (await isOneSignalBridgeMute()) {
      result.error = 'Pont OneSignal iOS muet (coque antérieure aux notifications)';
      return result;
    }
    
    // Check 3: RGPD consent
    const hasConsent = await hasRgpdConsent(userId);
    if (!hasConsent) {
      result.error = 'RGPD: No data_processing consent';
      logger.warn('[pushTokenService] ❌ RGPD: No data_processing consent, aborting detailed registration');
      return result;
    }
    // ✅ Grant privacy consent to Median/OneSignal bridge
    grantOneSignalPrivacyConsent();
    logger.info('[pushTokenService] ✅ RGPD consent confirmed (detailed), proceeding');
    
    // Register + login (modern API)
    medianNativeBridge.registerPushNotifications();
    loginOneSignal(userId);
    result.externalIdSet = true;
    
    const platform = medianNativeBridge.getMedianPlatform() || 'unknown';
    const isIOS = platform === 'ios';
    const maxRetries = isIOS ? IOS_MAX_RETRIES : DEFAULT_MAX_RETRIES;
    const baseDelayMs = isIOS ? IOS_BASE_DELAY_MS : DEFAULT_BASE_DELAY_MS;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      result.attempt = attempt;
      const delay = baseDelayMs * attempt;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      try {
        const oneSignalInfo = await medianNativeBridge.getOneSignalInfo();
        result.oneSignalInfo = oneSignalInfo || undefined;
        
        if (oneSignalInfo?.userId) {
          // iOS: Vérifier subscription_id
          if (isIOS && !oneSignalInfo.subscriptionId) {
            continue; // Réessayer
          }
          
          result.playerId = oneSignalInfo.userId;
          result.subscriptionId = oneSignalInfo.subscriptionId;
          
          // ✅ MULTI-ACCOUNT: Désactiver les tokens de ce device pour AUTRES utilisateurs
          // Utiliser subscription_id (stable) au lieu de onesignal_user_id (change avec login)
          let detailedCleanupQuery = supabase
            .from('user_push_tokens')
            .update({ 
              is_active: false, 
              updated_at: new Date().toISOString() 
            })
            .neq('user_id', userId);
          
          if (oneSignalInfo.subscriptionId) {
            detailedCleanupQuery = detailedCleanupQuery.eq('subscription_id', oneSignalInfo.subscriptionId);
            logger.debug('[pushTokenService] 🔄 Detailed cleanup by subscription_id');
          } else {
            detailedCleanupQuery = detailedCleanupQuery.eq('onesignal_user_id', oneSignalInfo.userId);
            logger.debug('[pushTokenService] 🔄 Detailed cleanup by onesignal_user_id (fallback)');
          }
          
          await detailedCleanupQuery;
          
          // ✅ FIX MULTI-DEVICE: Utiliser subscription_id comme clé unique par device
          const detailedTokenData = {
            user_id: userId,
            onesignal_user_id: oneSignalInfo.userId,
            subscription_id: oneSignalInfo.subscriptionId || null,
            platform,
            is_active: true,
            last_used_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          let error: any = null;

          if (oneSignalInfo.subscriptionId) {
            const result = await supabase
              .from('user_push_tokens')
              .upsert(detailedTokenData, { onConflict: 'user_id,subscription_id' });
            error = result.error;

            if (error?.code === '23505') {
              logger.warn('[pushTokenService] ⚠️ unique_violation on detailed upsert, forcing cleanup & retry...');
              await supabase
                .from('user_push_tokens')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('subscription_id', oneSignalInfo.subscriptionId)
                .neq('user_id', userId);
              const retry = await supabase
                .from('user_push_tokens')
                .upsert(detailedTokenData, { onConflict: 'user_id,subscription_id' });
              error = retry.error;
            }
          } else {
            logger.warn('[pushTokenService] ⚠️ No subscription_id, using legacy insert/update (detailed)');
            const { data: existing } = await supabase
              .from('user_push_tokens')
              .select('id')
              .eq('user_id', userId)
              .eq('onesignal_user_id', oneSignalInfo.userId)
              .maybeSingle();

            if (existing) {
              const result = await supabase.from('user_push_tokens')
                .update(detailedTokenData).eq('id', existing.id);
              error = result.error;
            } else {
              const result = await supabase.from('user_push_tokens')
                .insert(detailedTokenData);
              error = result.error;
            }
          }

          if (!error) {
            result.success = true;
            result.dbUpsertSuccess = true;
            return result;
          } else {
            result.error = `DB error: ${error.message}`;
          }
        }
      } catch (error) {
        result.error = `Attempt ${attempt} error: ${error}`;
      }
    }
    
    if (!result.error) {
      result.error = 'OneSignal userId never obtained after all retries';
    }
    
    return result;
  },

  async deactivatePushToken(userId: string): Promise<void> {
    try {
      if (!medianNativeBridge.isMedianNative()) {
        logger.debug('[pushTokenService] Not in Median environment, skipping deactivation');
        return;
      }
      
      const oneSignalInfo = await medianNativeBridge.getOneSignalInfo();
      
      // ✅ FIX: Utiliser les valeurs sauvegardées comme fallback
      const savedOneSignalUserId = localStorage.getItem('last_onesignal_user_id');
      const savedSubscriptionId = localStorage.getItem('last_onesignal_subscription_id');
      
      const effectiveUserId = oneSignalInfo?.userId || savedOneSignalUserId;
      const effectiveSubscriptionId = oneSignalInfo?.subscriptionId || savedSubscriptionId;
      
      if (effectiveUserId) {
        // ✅ FIX MULTI-DEVICE: Préférer subscription_id pour désactiver le bon device
        let deactivateQuery = supabase
          .from('user_push_tokens')
          .update({ 
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        
        if (effectiveSubscriptionId) {
          deactivateQuery = deactivateQuery.eq('subscription_id', effectiveSubscriptionId);
        } else {
          deactivateQuery = deactivateQuery.eq('onesignal_user_id', effectiveUserId);
        }
        
        const { error } = await deactivateQuery;
          
        if (error) {
          logger.warn('[pushTokenService] Deactivate error:', error);
        } else {
          logger.info('[pushTokenService] ✅ Token deactivated for user on this device');
        }
        
        // ✅ NOTE: localStorage cleanup is now handled by the caller (useAuthLogic)
        // to ensure it only happens after successful deactivation
      } else {
        logger.warn('[pushTokenService] ⚠️ Cannot deactivate: no OneSignal info available (even from localStorage)');
        // Note: on ne désactive PAS tous les tokens du user pour éviter de désactiver ses autres devices
      }
    } catch (error) {
      logger.warn('[pushTokenService] Deactivate error', error);
      throw error; // ✅ Propagate error so caller can handle cleanup
    }
  },

  /**
   * Vérifie si le token actuel est actif côté serveur et re-enregistre si nécessaire
   * Appelé au démarrage de l'app pour récupérer les tokens marqués inactifs
   */
  async checkAndRefreshToken(userId: string): Promise<boolean> {
    logger.info('[pushTokenService] 🔍 Checking token status for user:', userId);
    
    if (!medianNativeBridge.isMedianNative()) {
      logger.debug('[pushTokenService] Not in Median environment, skipping check');
      return false;
    }
    
    try {
      const oneSignalInfo = await medianNativeBridge.getOneSignalInfo();
      
      if (!oneSignalInfo?.userId) {
        logger.warn('[pushTokenService] No OneSignal user ID, triggering fresh registration...');
        return await this.registerPushToken(userId);
      }
      
      // Check if token is active in database (multi-device safe)
      let tokenQuery = supabase
        .from('user_push_tokens')
        .select('is_active, subscription_id')
        .eq('user_id', userId);

      if (oneSignalInfo.subscriptionId) {
        // Multi-device safe: target this specific device
        tokenQuery = tokenQuery.eq('subscription_id', oneSignalInfo.subscriptionId);
      } else {
        // Legacy fallback
        tokenQuery = tokenQuery.eq('onesignal_user_id', oneSignalInfo.userId);
      }

      const { data: existingToken, error } = await tokenQuery.maybeSingle();
      
      if (error || !existingToken) {
        logger.info('[pushTokenService] Token not found in DB, registering...');
        return await this.registerPushToken(userId);
      }
      
      if (!existingToken.is_active) {
        logger.info('[pushTokenService] ⚠️ Token marked inactive (possibly failed delivery), re-registering...');
        // Force fresh registration to get new APNs/FCM token
        return await this.registerPushToken(userId);
      }

      // ✅ AUTO-RÉPARATION: Détecter les tokens "dégradés" — actifs en DB mais rejetés répétitivement
      // par OneSignal (invalid_player_ids dans les 3 derniers push_delivery_logs).
      // Si tous les envois récents ont été rejetés → forcer une re-registration même si is_active=true.
      if (existingToken.subscription_id) {
        try {
          const { data: recentLogs } = await supabase
            .from('push_delivery_logs')
            .select('error_detail')
            .eq('user_id', userId)
            .eq('subscription_id', existingToken.subscription_id)
            .order('sent_at', { ascending: false })
            .limit(3);

          if (recentLogs && recentLogs.length >= 3) {
            const allRejected = recentLogs.every(log => {
              const detail = log.error_detail as { invalid_tokens?: string[] } | null;
              return detail?.invalid_tokens?.includes(existingToken.subscription_id);
            });

            if (allRejected) {
              logger.warn('[pushTokenService] ⚠️ Token is active in DB but rejected by OneSignal in last 3 pushes, forcing re-registration');
              // Marquer inactif d'abord pour éviter que sync-push-token-onesignal le réactive
              await supabase
                .from('user_push_tokens')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('user_id', userId)
                .eq('subscription_id', existingToken.subscription_id);
              return await this.registerPushToken(userId);
            }
          }
        } catch (degradedCheckErr) {
          // Non-bloquant: continuer même si la vérification échoue
          logger.warn('[pushTokenService] Could not check degraded token state:', degradedCheckErr);
        }
      }
      
      logger.info('[pushTokenService] ✅ Token is active, no action needed');
      return true;
    } catch (err) {
      logger.error('[pushTokenService] Error checking token status:', err);
      return false;
    }
  },

  /**
   * Nettoie les tokens invalides pour un utilisateur (appelé manuellement)
   * Retourne le résultat du nettoyage
   */
  async cleanupInvalidTokens(userId: string): Promise<{
    success: boolean;
    cleaned: number;
    valid: number;
    total: number;
    error?: string;
  }> {
    logger.info('[pushTokenService] 🧹 Starting manual cleanup for user:', userId);
    
    try {
      const { data, error } = await invokeWithTimeout<{
        cleaned?: number;
        valid?: number;
        total?: number;
      }>('cleanup-invalid-push-tokens', { userId }, { timeout: 15000, maxRetries: 2 });

      if (error) {
        logger.error('[pushTokenService] Cleanup error:', error);
        return { 
          success: false, 
          cleaned: 0, 
          valid: 0, 
          total: 0, 
          error: error.message 
        };
      }

      logger.info('[pushTokenService] Cleanup result:', {
        cleaned: data?.cleaned || 0,
        valid: data?.valid || 0,
        total: data?.total || 0
      });

      return {
        success: true,
        cleaned: data?.cleaned || 0,
        valid: data?.valid || 0,
        total: data?.total || 0
      };
    } catch (err) {
      logger.error('[pushTokenService] Cleanup exception:', err);
      return { 
        success: false, 
        cleaned: 0, 
        valid: 0, 
        total: 0, 
        error: err instanceof Error ? err.message : 'Unknown error' 
      };
    }
  },

  /**
   * Vérifie si l'utilisateur doit être re-prompté pour l'enregistrement push
   * Retourne true si:
   * - Aucun token actif
   * - OU external_id non vérifié depuis plus de 7 jours
   * 
   * Utilise localStorage pour limiter les vérifications à 1x/semaine
   */
  async shouldPromptForPushReregistration(userId: string): Promise<boolean> {
    logger.info('[pushTokenService] 🔍 Weekly check: shouldPromptForPushReregistration');
    
    if (!medianNativeBridge.isMedianNative()) {
      logger.debug('[pushTokenService] Not in Median environment, skipping weekly check');
      return false;
    }

    const DAYS_BETWEEN_CHECKS = 7;
    const lastPromptKey = `push_external_id_last_verified_${userId}`;
    
    try {
      // 1. Vérifier le localStorage pour la dernière vérification
      const lastVerified = localStorage.getItem(lastPromptKey);
      
      if (lastVerified) {
        const daysSince = (Date.now() - parseInt(lastVerified)) / (1000 * 60 * 60 * 24);
        if (daysSince < DAYS_BETWEEN_CHECKS) {
          logger.debug(`[pushTokenService] Last verified ${daysSince.toFixed(1)} days ago, skipping`);
          return false;
        }
      }
      
      logger.info('[pushTokenService] Weekly check triggered (>7 days or first check)');
      
      // 2. Vérifier en base si le token a un external_id synced
      const { data: token, error } = await supabase
        .from('user_push_tokens')
        .select('onesignal_user_id, subscription_id, last_sync_verified_at')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        logger.warn('[pushTokenService] Error checking token:', error);
        return false;
      }
      
      if (!token?.onesignal_user_id) {
        logger.info('[pushTokenService] No active token found, should re-register');
        return true;
      }
      
      // 3. Vérifier via API si external_id est bien set côté OneSignal
      logger.info('[pushTokenService] Calling check-onesignal-sync for verification...');
      const { data: syncResult, error: syncError } = await supabase.functions.invoke('check-onesignal-sync', {
        body: { action: 'check_single', userId, onesignalId: token.onesignal_user_id }
      });
      
      if (syncError) {
        logger.warn('[pushTokenService] Sync check error:', syncError);
        return false; // Ne pas redemander en cas d'erreur API
      }
      
      if (syncResult?.isSynced) {
        logger.info('[pushTokenService] ✅ External ID verified, marking as checked');
        localStorage.setItem(lastPromptKey, Date.now().toString());
        return false;
      }
      
      logger.info('[pushTokenService] ⚠️ External ID NOT synced, should re-register');
      return true;
      
    } catch (err) {
      logger.error('[pushTokenService] Weekly check error:', err);
      return false;
    }
  },

  /**
   * Marque la vérification comme effectuée pour un utilisateur
   */
  markSyncVerified(userId: string): void {
    const lastPromptKey = `push_external_id_last_verified_${userId}`;
    try {
      localStorage.setItem(lastPromptKey, Date.now().toString());
      logger.debug('[pushTokenService] Sync verification marked');
    } catch {
      // Ignore localStorage errors
    }
  },

  /**
   * ✅ PHASE 6: Vérifie et corrige l'association push après login
   * Utilisé pour confirmer que l'external_id est bien associé au bon compte
   * Retourne:
   * - isCorrect: true si external_id correspond au userId
   * - currentExternalId: la valeur actuelle de external_id côté OneSignal
   * - action: 'none' (déjà OK), 'fixed' (corrigé), 'failed' (échec correction)
   */
  async verifyPushAssociation(userId: string): Promise<{
    isCorrect: boolean;
    currentExternalId: string | null;
    action: 'none' | 'fixed' | 'failed';
  }> {
    logger.info('[pushTokenService] 🔍 Verifying push association for:', userId.slice(0, 8));
    
    if (!medianNativeBridge.isMedianNative()) {
      return { isCorrect: false, currentExternalId: null, action: 'none' };
    }

    // ✅ DEDUPLICATION: Check if already verified in this session (5min window)
    const sessionCheckKey = `push_verify_session_${userId}`;
    const lastSessionCheck = localStorage.getItem(sessionCheckKey);
    if (lastSessionCheck) {
      const minutesSince = (Date.now() - parseInt(lastSessionCheck)) / (1000 * 60);
      if (minutesSince < 5) {
        logger.debug(`[pushTokenService] verifyPushAssociation: Already checked ${minutesSince.toFixed(1)}min ago, skipping`);
        return { isCorrect: true, currentExternalId: null, action: 'none' };
      }
    }

    try {
      // 1. Get current token from database
      const { data: token } = await supabase
        .from('user_push_tokens')
        .select('onesignal_user_id, subscription_id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!token?.onesignal_user_id) {
        logger.debug('[pushTokenService] verifyPushAssociation: No active token found');
        return { isCorrect: false, currentExternalId: null, action: 'none' };
      }

      // ✅ BEST PRACTICE: Use invokeWithTimeout with enhanced error handling
      // 2. Check via edge function if external_id matches
      const { data: checkResult, error: checkError } = await invokeWithTimeout<{
        isSynced?: boolean;
        currentExternalId?: string | null;
        error?: string;
      }>('check-onesignal-sync', { 
        action: 'check_single', 
        userId, 
        onesignalId: token.onesignal_user_id 
      }, { timeout: 10000, maxRetries: 2 });

      if (checkError) {
        logger.warn('[pushTokenService] verifyPushAssociation check failed:', {
          message: checkError.message,
          code: (checkError as any).code || 'unknown',
          details: (checkError as any).details
        });
        return { isCorrect: false, currentExternalId: null, action: 'failed' };
      }

      // Mark session check done
      try {
        localStorage.setItem(sessionCheckKey, Date.now().toString());
      } catch { /* ignore */ }

      if (checkResult?.isSynced) {
        // All good, external_id matches
        this.markSyncVerified(userId);
        logger.info('[pushTokenService] ✅ Push association verified OK');
        return { isCorrect: true, currentExternalId: checkResult.currentExternalId ?? null, action: 'none' };
      }

      // 3. External_id mismatch - try to fix it
      logger.info('[pushTokenService] ⚠️ External_id mismatch, attempting fix...', {
        expected: userId.slice(0, 8),
        actual: checkResult?.currentExternalId?.slice(0, 8) || 'null'
      });

      // ✅ BEST PRACTICE: Use invokeWithTimeout for sync call
      const { data: syncResult, error: syncError } = await invokeWithTimeout<{
        synced?: boolean;
        message?: string;
      }>('sync-push-token-onesignal', { 
        userId, 
        onesignalPlayerId: token.onesignal_user_id,
        subscriptionId: token.subscription_id
      }, { timeout: 15000, maxRetries: 3 });

      if (syncError || !syncResult?.synced) {
        logger.warn('[pushTokenService] verifyPushAssociation fix failed:', {
          error: syncError?.message,
          result: syncResult
        });
        return { 
          isCorrect: false, 
          currentExternalId: checkResult?.currentExternalId ?? null, 
          action: 'failed' 
        };
      }

      // Fix successful
      this.markSyncVerified(userId);
      logger.info('[pushTokenService] ✅ External_id fixed successfully');
      return { isCorrect: true, currentExternalId: userId, action: 'fixed' };

    } catch (error) {
      logger.error('[pushTokenService] verifyPushAssociation error:', error);
      return { isCorrect: false, currentExternalId: null, action: 'failed' };
    }
  },

  /**
   * 🔧 Auto-Repair: Détecte et corrige automatiquement les incohérences push
   * Compare l'état du device (OneSignal) vs cache localStorage vs base de données
   * 
   * CAS 1: Device onesignal_user_id != cache → re-register complet
   * CAS 2: Token en base mais external_id null/wrong côté OneSignal → sync
   * CAS 3: Aucun token actif en base mais OneSignal initialisé → registerPushToken
   * 
   * Cooldown propre de 2 min (séparé de verifyPushAssociation)
   */
  async autoRepairPushState(userId: string): Promise<{
    action: 'none' | 'reregistered' | 'synced' | 'registered' | 'skipped' | 'error';
    reason: string;
  }> {
    const COOLDOWN_KEY = `push_auto_repair_last_${userId}`;
    const COOLDOWN_MS = 30 * 1000; // 30 secondes (réduit de 2 min pour gérer les switches de compte)

    logger.info('[pushTokenService] 🔧 autoRepairPushState starting for:', userId.slice(0, 8));

    // 0. Not native? Skip
    if (!medianNativeBridge.isMedianNative()) {
      logger.debug('[pushTokenService] autoRepair: Not in Median, skipping');
      return { action: 'skipped', reason: 'not_native' };
    }

    // 1. Get device state FIRST (avant le cooldown check) pour détecter les changements de compte
    let deviceInfo: Awaited<ReturnType<typeof medianNativeBridge.getOneSignalInfo>>;
    try {
      deviceInfo = await medianNativeBridge.getOneSignalInfo();
    } catch {
      deviceInfo = null;
    }

    // Détecter un changement de compte : si l'external_id OneSignal sur le device
    // est différent de ce qu'on a en cache, bypass le cooldown
    const cachedUserId = localStorage.getItem('last_onesignal_user_id');
    const isAccountSwitch = !!(cachedUserId && deviceInfo?.userId && cachedUserId !== deviceInfo.userId);

    if (!isAccountSwitch) {
      // Cooldown check normal (seulement si pas de changement de compte)
      try {
        const lastRun = localStorage.getItem(COOLDOWN_KEY);
        if (lastRun) {
          const elapsed = Date.now() - parseInt(lastRun);
          if (elapsed < COOLDOWN_MS) {
            logger.debug(`[pushTokenService] autoRepair: Cooldown active (${Math.round((COOLDOWN_MS - elapsed) / 1000)}s remaining)`);
            return { action: 'skipped', reason: 'cooldown' };
          }
        }
      } catch { /* ignore */ }
    } else {
      logger.info('[pushTokenService] autoRepair: 🔀 Account switch detected, bypassing cooldown', {
        cached: cachedUserId?.slice(0, 8),
        device: deviceInfo?.userId?.slice(0, 8),
      });
    }

    // Mark cooldown immediately to prevent concurrent runs
    try { localStorage.setItem(COOLDOWN_KEY, Date.now().toString()); } catch { /* ignore */ }

    try {
      
      if (!deviceInfo?.userId) {
        logger.warn('[pushTokenService] autoRepair: No OneSignal userId on device, triggering full registration');
        // CAS 3 variant: OneSignal not initialized yet
        const success = await this.registerPushToken(userId);
        return { 
          action: success ? 'registered' : 'error', 
          reason: 'no_device_onesignal_id' 
        };
      }

      logger.debug('[pushTokenService] autoRepair: Device state:', {
        userId: deviceInfo.userId.slice(0, 8),
        subscriptionId: deviceInfo.subscriptionId?.slice(0, 8) || 'N/A',
        isAccountSwitch,
      });

      // CAS 1: Device onesignal_user_id != cache localStorage (déjà détecté avant le cooldown)
      // isAccountSwitch est true → re-register complètement
      if (isAccountSwitch) {
        logger.info('[pushTokenService] autoRepair: CAS 1 - Account switch, re-registering...', {
          cached: cachedUserId?.slice(0, 8),
          device: deviceInfo.userId.slice(0, 8),
        });
        
        // Update cache first
        try {
          localStorage.setItem('last_onesignal_user_id', deviceInfo.userId);
          if (deviceInfo.subscriptionId) {
            localStorage.setItem('last_onesignal_subscription_id', deviceInfo.subscriptionId);
          }
        } catch { /* ignore */ }

        // Re-register completely with current userId
        const success = await this.registerPushToken(userId);
        if (success) {
          this.markSyncVerified(userId);
        }
        return { 
          action: success ? 'reregistered' : 'error', 
          reason: 'account_switch' 
        };
      }

      // 4. Check database state
      // On cherche d'abord avec is_active=true, et si rien trouvé on cherche is_active=false
      let tokenQuery = supabase
        .from('user_push_tokens')
        .select('id, onesignal_user_id, subscription_id, is_active')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (deviceInfo.subscriptionId) {
        tokenQuery = tokenQuery.eq('subscription_id', deviceInfo.subscriptionId);
      }

      const { data: dbToken, error: dbError } = await tokenQuery.maybeSingle();

      if (dbError) {
        logger.warn('[pushTokenService] autoRepair: DB query error:', dbError);
        return { action: 'error', reason: 'db_query_error' };
      }

      // CAS 3 étendu: Pas de token en DB OU token is_active=false → re-enregistrement
      if (!dbToken || !dbToken.is_active) {
        const reason = !dbToken ? 'no_token_in_db' : 'token_inactive_in_db';
        logger.info(`[pushTokenService] autoRepair: CAS 3 - ${reason}, registering`);
        const success = await this.registerPushToken(userId);
        if (success) {
          this.markSyncVerified(userId);
        }
        return { 
          action: success ? 'registered' : 'error', 
          reason 
        };
      }

      // CAS 2: Token in DB but external_id null/wrong on OneSignal side
      // Check via edge function
      logger.debug('[pushTokenService] autoRepair: Token found in DB, checking external_id sync...');
      const { data: checkResult, error: checkError } = await invokeWithTimeout<{
        isSynced?: boolean;
        currentExternalId?: string | null;
      }>('check-onesignal-sync', { 
        action: 'check_single', 
        userId, 
        onesignalId: dbToken.onesignal_user_id 
      }, { timeout: 10000, maxRetries: 2 });

      if (checkError) {
        logger.warn('[pushTokenService] autoRepair: Sync check failed:', checkError.message);
        // Not critical, just log and continue
        return { action: 'error', reason: 'sync_check_failed' };
      }

      if (checkResult?.isSynced) {
        logger.info('[pushTokenService] autoRepair: ✅ Everything in sync, no action needed');
        this.markSyncVerified(userId);
        // Update cache to be safe
        try {
          localStorage.setItem('last_onesignal_user_id', deviceInfo.userId);
          if (deviceInfo.subscriptionId) {
            localStorage.setItem('last_onesignal_subscription_id', deviceInfo.subscriptionId);
          }
        } catch { /* ignore */ }
        return { action: 'none', reason: 'already_synced' };
      }

      // External ID is null or wrong → fix it
      logger.info('[pushTokenService] autoRepair: CAS 2 - external_id mismatch, syncing...', {
        expected: userId.slice(0, 8),
        actual: checkResult?.currentExternalId?.slice(0, 8) || 'null',
      });

      const { data: syncResult, error: syncError } = await invokeWithTimeout<{
        synced?: boolean;
        message?: string;
      }>('sync-push-token-onesignal', { 
        userId, 
        onesignalPlayerId: dbToken.onesignal_user_id,
        subscriptionId: dbToken.subscription_id,
        autoSync: true
      }, { timeout: 15000, maxRetries: 3 });

      if (syncError || !syncResult?.synced) {
        logger.warn('[pushTokenService] autoRepair: Sync failed:', syncError?.message || syncResult?.message);
        return { action: 'error', reason: 'sync_failed' };
      }

      this.markSyncVerified(userId);
      logger.info('[pushTokenService] autoRepair: ✅ external_id synced successfully');
      return { action: 'synced', reason: 'external_id_fixed' };

    } catch (error) {
      logger.error('[pushTokenService] autoRepair: Unexpected error:', error);
      return { action: 'error', reason: 'unexpected_error' };
    }
  }
};
